import { useEffect } from 'react';
import { Outlet, RouterProvider, ScrollRestoration, createBrowserRouter } from 'react-router-dom';
import { TooltipProvider } from '@/components/ui';
import { Toaster } from '@/components/ui/Toaster';
import { setUnauthenticatedHandler } from '@/lib/api';
import { readSession } from '@/lib/tokens';
import { routes } from '@/routes';
import { restoreSession, sessionLost } from '@/store/slices/authSlice';
import { initTheme } from '@/store/slices/uiSlice';
import { useAppDispatch, useAppSelector } from '@/store/store';

/**
 * Every page opens at the top; Back and Forward return to where you were.
 *
 * The router keeps the window where it is when the route changes, so pressing "Check
 * my CV" halfway down the form opened the result halfway down too — the score, the
 * thing the whole page is for, scrolled off above the fold. ScrollRestoration resets
 * a new page to the top and remembers each visited one by its history entry, so Back
 * lands where the person left off rather than at the top of a long report.
 */
function RootLayout() {
  return (
    <>
      <ScrollRestoration />
      <Outlet />
    </>
  );
}

const router = createBrowserRouter([{ element: <RootLayout />, children: routes }]);

export function App() {
  const dispatch = useAppDispatch();
  const theme = useAppSelector((state) => state.ui.theme);

  useEffect(() => {
    initTheme(theme);
    // Only on mount: `setTheme` already applies the class when the user changes it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // A failed refresh anywhere in the app clears Redux, not just localStorage.
    setUnauthenticatedHandler(() => dispatch(sessionLost()));

    if (readSession()) void dispatch(restoreSession());
    else dispatch(sessionLost());
  }, [dispatch]);

  return (
    <TooltipProvider delayDuration={250}>
      <RouterProvider router={router} />
      <Toaster />
    </TooltipProvider>
  );
}
